import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ReviewStats {
  totalReviews: number;
  avgRating: number;
}

interface RecentReview {
  rating: number;
  content: string | null;
  title: string | null;
  created_at: string;
  customerName: string | null;
}

export const useStoreReviews = () => {
  const { data: stats } = useQuery<ReviewStats>({
    queryKey: ["store-review-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("rating");

      if (error) throw error;

      if (!data || data.length === 0) {
        return { totalReviews: 0, avgRating: 0 };
      }

      const totalReviews = data.length;
      const avgRating =
        Math.round(
          (data.reduce((sum, r) => sum + r.rating, 0) / totalReviews) * 10
        ) / 10;

      return { totalReviews, avgRating };
    },
    staleTime: 1000 * 60 * 30, // 30 min cache
  });

  const { data: recentReviews } = useQuery<RecentReview[]>({
    queryKey: ["store-recent-reviews"],
    queryFn: async () => {
      const { data: reviews, error } = await supabase
        .from("reviews")
        .select("rating, content, title, created_at, customer_id")
        .order("created_at", { ascending: false })
        .limit(5);

      if (error) throw error;
      if (!reviews || reviews.length === 0) return [];

      // Fetch profile names for the reviewers
      const customerIds = reviews.map((r) => r.customer_id);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, display_name, full_name")
        .in("id", customerIds);

      const profileMap = new Map(
        (profiles || []).map((p) => [
          p.id,
          p.display_name || p.full_name || null,
        ])
      );

      return reviews.map((r) => ({
        rating: r.rating,
        content: r.content,
        title: r.title,
        created_at: r.created_at,
        customerName: profileMap.get(r.customer_id) || null,
      }));
    },
    staleTime: 1000 * 60 * 30,
    enabled: (stats?.totalReviews ?? 0) > 0,
  });

  return {
    totalReviews: stats?.totalReviews ?? 0,
    avgRating: stats?.avgRating ?? 0,
    recentReviews: recentReviews ?? [],
  };
};
